// src/views/Dashboard.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE } from "../apiConfig";

import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";

import KPI from "../components/KPI";
import LoaderPremiumLogo from "../components/LoaderPremiumLogo";
import MapaQuintanaRoo from "./MapaQuintanaRoo";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

export default function Dashboard() {
  const [inventario, setInventario] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE}/inventario`);

        const data = res.data || [];
        const normalizado = data.map((eq) => ({
          ...eq,
          condicion: eq.condicion || eq.estadoEquipo || "N/A",
        }));

        setInventario(normalizado);
        window.__INVENTARIO_GLOBAL__ = normalizado;
      } catch (error) {
        console.error("❌ Error cargando inventario:", error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  if (loading) return <LoaderPremiumLogo />;

  // ============ KPI GENERALES ============
  const totalEquipos = inventario.length;

  const equiposPorMunicipio = inventario.reduce((acc, item) => {
    const key = item.municipio || "Sin municipio";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const criticos = inventario.filter(
    (i) => (i.estadoEquipo || "").toUpperCase() !== "BUENO"
  );

  const saludGlobal =
    totalEquipos === 0
      ? 0
      : Math.round(((totalEquipos - criticos.length) / totalEquipos) * 100);

  // ============ STOCK ============
  const stock = inventario.filter(
    (i) => i.proyecto?.toUpperCase() === "STOCK"
  );
  const equiposAsignados = inventario.filter(
    (i) => i.proyecto?.toUpperCase() !== "STOCK"
  );

  const stockTotal = stock.length;
  const asignadosTotal = equiposAsignados.length;

  // ============ MÓDULOS POR OFICINA ============
  const modulosPorOficina = inventario.reduce((acc, item) => {
    const oficina = item.oficina || "Sin oficina";
    const modulo = item.modulo || "Sin módulo";

    if (!acc[oficina]) acc[oficina] = {};
    if (!acc[oficina][modulo]) acc[oficina][modulo] = [];

    acc[oficina][modulo].push(item);
    return acc;
  }, {});

  const diagnosticoModulos = [];

  Object.entries(modulosPorOficina).forEach(([oficina, modulos]) => {
    Object.entries(modulos).forEach(([modulo, equipos]) => {
      const faltanCantidad = equipos.length >= 11 ? 0 : 11 - equipos.length;

      diagnosticoModulos.push({
        oficina,
        municipio: equipos[0]?.municipio || "N/D",
        modulo,
        totalActual: equipos.length,
        faltanCantidad,
        completo: faltanCantidad === 0,
      });
    });
  });

  const totalModulosDiagnostico = diagnosticoModulos.length;
  const modulosCompletos = diagnosticoModulos.filter((m) => m.completo).length;
  const modulosIncompletos = diagnosticoModulos.filter((m) => !m.completo);

  // ============ MÓDULOS POR MUNICIPIO ============
  const muniLabelByKey = {};
  const moduloSetByMuniKey = {};

  inventario.forEach((item) => {
    let rawMuni = item.municipio || "Sin municipio";
    let rawModulo = item.modulo;

    if (!rawModulo) return;

    const moduloNorm = String(rawModulo).trim();
    if (isNaN(Number(moduloNorm))) return;

    const muniKey = rawMuni.trim().toUpperCase();

    if (!moduloSetByMuniKey[muniKey]) {
      moduloSetByMuniKey[muniKey] = new Set();
      muniLabelByKey[muniKey] = rawMuni.trim();
    }

    moduloSetByMuniKey[muniKey].add(moduloNorm);
  });

  const modulosPorMunicipioCount = {};
  Object.entries(moduloSetByMuniKey).forEach(([key, set]) => {
    modulosPorMunicipioCount[muniLabelByKey[key]] = set.size;
  });

  const totalModulosExistentes = Object.values(
    modulosPorMunicipioCount
  ).reduce((acc, n) => acc + n, 0);

  // ============ REEMPLAZO ============
  function calcularEstatusReemplazo(equipo) {
    const estado = (equipo.condicion || "").toUpperCase();
    const fecha = equipo.fechaAdquisicion
      ? new Date(equipo.fechaAdquisicion)
      : null;

    let años = 0;
    if (fecha instanceof Date && !isNaN(fecha.valueOf())) {
      const diff = Date.now() - fecha.getTime();
      años = Math.floor(diff / (1000 * 60 * 60 * 24 * 365));
    }

    if (estado === "MALO" || estado === "NO OPERATIVO" || estado === "EN REPARACIÓN")
      return "REEMPLAZO";

    if (años >= 6) return "URGENTE";
    if (años >= 4) return "REEMPLAZO";

    return "OK";
  }

  const equiposParaReemplazo = inventario
    .map((eq) => ({ ...eq, estatusReemplazo: calcularEstatusReemplazo(eq) }))
    .filter((eq) => eq.estatusReemplazo !== "OK");

  // ============ RENDER ============
  return (
    <div className="min-h-screen - from-slate-100 via-slate-100 to-slate-200 py-10 px-4">
      <div className="max-w-7xl mx-auto space-y-8">

        {/* HEADER */}
       <header className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 border border-slate-800 p-8 shadow-2xl">
  {/* grid HUD */}
  <div className="absolute inset-0 opacity-[0.08] 
    [background-image:linear-gradient(to_right,#ffffff1a_1px,transparent_1px),
    linear-gradient(to_bottom,#ffffff1a_1px,transparent_1px)]
    [background-size:24px_24px]"
  />

  <div className="relative z-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
    <div>
      <p className="text-[10px] uppercase tracking-[0.35em] text-blue-400">
        Panel Ejecutivo
      </p>

      <h1 className="text-3xl font-extrabold text-white mt-1">
        Dashboard de Inventario
      </h1>

      <p className="text-sm text-slate-400 mt-2">
        IP DEPOT · Infraestructura tecnológica IMOVEQROO
      </p>
    </div>

    {/* Tarjeta resumen flotante */}
    <ExecutiveSummary
      total={totalEquipos}
      stock={stockTotal}
      asignados={asignadosTotal}
      salud={saludGlobal}
    />
  </div>
</header>




        {/* TARJETAS KPI */}
                        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                <KPI title="Equipos Totales" value={totalEquipos} icon="📦" />
                <KPI title="Equipos en Stock" value={stockTotal} icon="📊" />
                <KPI title="Equipos Asignados" value={asignadosTotal} icon="🏢" />

                <KPI
                    title="Salud Global"
                    value={`${saludGlobal}%`}
                    icon="💡"
                    dominant
                    percentage={saludGlobal}
                />

                <KPI title="Total Módulos" value={totalModulosExistentes} icon="🧱" />
                </section>


        {/* MÓDULOS POR MUNICIPIO */}
        <section className="relative overflow-hidden rounded-3xl bg-white/40 backdrop-blur-2xl border border-white/60 shadow-[0_18px_45px_rgba(15,23,42,0.18)] p-6">
          {/* blobs */}
          <div className="absolute -top-24 -right-10 w-64 h-64 bg-gradient-to-br from-sky-200/60 via-transparent to-transparent rounded-full blur-3xl" />
          <div className="absolute -bottom-28 -left-16 w-72 h-72 bg-gradient-to-tr from-indigo-200/60 via-transparent to-transparent rounded-full blur-3xl" />

          <div className="relative z-10">
            <h3 className="text-sm font-semibold text-slate-900 mb-1">
              Módulos por municipio
            </h3>

            <p className="text-xs text-slate-500 mb-4">
              Cantidad real de módulos activos por municipio.
            </p>

            <div className="space-y-2 text-sm">
              {Object.entries(modulosPorMunicipioCount).map(([muni, total]) => (
                <div
                  key={muni}
                  className="flex justify-between border-b border-slate-200/60 pb-1"
                >
                  <span className="text-slate-700">{muni}</span>
                  <span className="font-semibold text-slate-900">{total}</span>
                </div>
              ))}

              <div className="border-t border-slate-200/80 pt-2 mt-2 flex justify-between font-bold">
                <span className="text-slate-900">Total general</span>
                <span>{totalModulosExistentes}</span>
              </div>
            </div>
          </div>
        </section>

        {/* GRÁFICA: EQUIPOS POR MUNICIPIO */}
        <section className="relative overflow-hidden rounded-3xl bg-white/40 backdrop-blur-2xl border border-white/60 shadow-[0_18px_45px_rgba(15,23,42,0.18)] p-8">
          <div className="absolute -top-24 -right-10 w-64 h-64 bg-gradient-to-br from-cyan-200/60 via-transparent to-transparent rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-16 w-72 h-72 bg-gradient-to-tr from-blue-200/60 via-transparent to-transparent rounded-full blur-3xl" />

          <div className="relative z-10">
            <h3 className="text-lg font-bold text-slate-800 mb-2 tracking-tight">
              Equipos por municipio
            </h3>

            <p className="text-sm text-slate-500 mb-6">
              Distribución total de equipos asignados por cada municipio.
            </p>

            <div className="w-full h-[420px]">
              <Bar
                data={{
                  labels: Object.keys(equiposPorMunicipio),
                  datasets: [
                    {
                      label: "Equipos por Municipio",
                      data: Object.values(equiposPorMunicipio),
                      backgroundColor: (ctx) => {
                        const gradient =
                          ctx.chart.ctx.createLinearGradient(0, 0, 0, 300);
                        gradient.addColorStop(0, "rgba(56,189,248,0.95)");
                        gradient.addColorStop(1, "rgba(191,219,254,0.35)");
                        return gradient;
                      },
                      borderRadius: 10,
                      borderSkipped: false,
                      maxBarThickness: 48,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  layout: {
                    padding: { top: 20, right: 20, bottom: 10, left: 10 },
                  },
                  scales: {
                    x: {
                      ticks: { color: "#64748b", font: { size: 11 } },
                      grid: { display: false },
                    },
                    y: {
                      ticks: { color: "#64748b", font: { size: 11 } },
                      grid: { color: "rgba(148,163,184,0.25)" },
                      border: { display: false },
                    },
                  },
                  plugins: {
                    legend: {
                      labels: {
                        color: "#334155",
                        font: { size: 13, weight: "600" },
                      },
                    },
                    tooltip: {
                      backgroundColor: "#0f172a",
                      titleColor: "#e5e7eb",
                      bodyColor: "#e5e7eb",
                      padding: 10,
                      cornerRadius: 10,
                      displayColors: false,
                    },
                  },
                }}
              />
            </div>
          </div>
        </section>

        {/* TABLA: EQUIPOS EN STOCK */}
        <section className="relative overflow-hidden rounded-3xl bg-white/40 backdrop-blur-2xl border border-white/60 shadow-[0_18px_45px_rgba(15,23,42,0.18)] p-6">
          <div className="absolute -top-24 -right-10 w-64 h-64 bg-gradient-to-br from-emerald-200/60 via-transparent to-transparent rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-16 w-72 h-72 bg-gradient-to-tr from-teal-200/60 via-transparent to-transparent rounded-full blur-3xl" />

          <div className="relative z-10">
            <h3 className="text-sm font-semibold mb-3 text-slate-900">
              Equipos en Stock (Detalle)
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              Equipos disponibles para asignación inmediata.
            </p>

            <div className="overflow-x-auto rounded-2xl border border-slate-200/70 bg-white/70">
              <table className="w-full text-xs">
                <thead className="bg-slate-50/90 text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="p-2 text-left">Equipo</th>
                    <th className="p-2 text-left">Marca</th>
                    <th className="p-2 text-left">Modelo</th>
                    <th className="p-2 text-left">Serie</th>
                    <th className="p-2 text-left">Condición</th>
                    <th className="p-2 text-left">Fecha Registro</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {stock.map((eq) => (
                    <tr key={eq.id}>
                      <td className="p-2">{eq.equipo}</td>
                      <td className="p-2">{eq.marca}</td>
                      <td className="p-2">{eq.modelo}</td>
                      <td className="p-2">{eq.serie}</td>
                      <td className="p-2">{eq.condicion}</td>
                      <td className="p-2">
                        {eq.createdAt
                          ? new Date(eq.createdAt).toLocaleDateString()
                          : "N/A"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* DIAGNÓSTICO DE MÓDULOS */}
        {totalModulosDiagnostico > 0 && (
          <section className="relative overflow-hidden rounded-3xl bg-white/40 backdrop-blur-2xl border border-white/60 shadow-[0_18px_45px_rgba(15,23,42,0.18)] p-6">
            <div className="absolute -top-24 -right-10 w-64 h-64 bg-gradient-to-br from-amber-200/60 via-transparent to-transparent rounded-full blur-3xl" />
            <div className="absolute -bottom-24 -left-16 w-72 h-72 bg-gradient-to-tr from-orange-200/60 via-transparent to-transparent rounded-full blur-3xl" />

            <div className="relative z-10">
              <h3 className="text-sm font-semibold mb-2 text-slate-900">
                Diagnóstico completo de módulos
              </h3>

              <div className="overflow-x-auto rounded-2xl border border-slate-200/70 bg-white/75 mt-3">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="p-2">Municipio</th>
                      <th className="p-2">Oficina</th>
                      <th className="p-2">Módulo</th>
                      <th className="p-2 text-center">Total actual</th>
                      <th className="p-2 text-center">Faltantes</th>
                      <th className="p-2 text-center">Estatus</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100">
                    {diagnosticoModulos.map((m, i) => (
                      <tr key={i}>
                        <td className="p-2">{m.municipio}</td>
                        <td className="p-2">{m.oficina}</td>
                        <td className="p-2">{m.modulo}</td>
                        <td className="p-2 text-center">{m.totalActual}</td>
                        <td className="p-2 text-center">{m.faltanCantidad}</td>
                        <td className="p-2 text-center">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                              m.completo
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-amber-100 text-amber-700"
                            }`}
                          >
                            {m.completo ? "Completo" : "Incompleto"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {/* EQUIPOS PARA REEMPLAZO */}
        <section className="relative overflow-hidden rounded-3xl bg-white/40 backdrop-blur-2xl border border-white/60 shadow-[0_18px_45px_rgba(15,23,42,0.18)] p-6">
          <div className="absolute -top-24 -right-10 w-64 h-64 bg-gradient-to-br from-rose-200/70 via-transparent to-transparent rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-16 w-72 h-72 bg-gradient-to-tr from-red-200/60 via-transparent to-transparent rounded-full blur-3xl" />

          <div className="relative z-10">
            <h3 className="text-sm font-semibold mb-3 text-slate-900">
              Equipos para Reemplazo
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              Equipos que requieren reemplazo por antigüedad, fallas o estado operativo.
            </p>

            {equiposParaReemplazo.length === 0 ? (
              <p className="text-center text-sm text-emerald-600 font-semibold py-3">
                ✔ Todos los equipos están en buen estado.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-slate-200/70 bg-white/75">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500 border-b">
                    <tr>
                      <th className="p-2 text-left">Equipo</th>
                      <th className="p-2 text-left">Marca</th>
                      <th className="p-2 text-left">Modelo</th>
                      <th className="p-2 text-left">Serie</th>
                      <th className="p-2 text-left">Estado</th>
                      <th className="p-2 text-left">F. Adquisición</th>
                      <th className="p-2 text-left">Prioridad</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100">
                    {equiposParaReemplazo.map((eq) => (
                      <tr key={eq.id}>
                        <td className="p-2">{eq.equipo}</td>
                        <td className="p-2">{eq.marca}</td>
                        <td className="p-2">{eq.modelo}</td>
                        <td className="p-2">{eq.serie}</td>
                        <td className="p-2">{eq.estadoEquipo}</td>
                        <td className="p-2">
                          {eq.fechaAdquisicion
                            ? new Date(eq.fechaAdquisicion).toLocaleDateString()
                            : "N/A"}
                        </td>
                        <td className="p-2">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                              eq.estatusReemplazo === "URGENTE"
                                ? "bg-red-100 text-red-700"
                                : "bg-amber-100 text-amber-700"
                            }`}
                          >
                            {eq.estatusReemplazo}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        {/* MAPA STARLINK */}
        <section className="relative overflow-hidden rounded-3xl bg-white/40 backdrop-blur-2xl border border-white/60 shadow-[0_18px_45px_rgba(15,23,42,0.22)] p-6">
          <div className="absolute -top-24 -right-10 w-64 h-64 bg-gradient-to-br from-sky-200/70 via-transparent to-transparent rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-16 w-72 h-72 bg-gradient-to-tr from-indigo-200/60 via-transparent to-transparent rounded-full blur-3xl" />

          <div className="relative z-10 space-y-4">

            {/* TARJETA PREMIUM DE COBERTURA (SOLO DISEÑO, MISMOS DATOS) */}
            <div className="w-full bg-gradient-to-r from-sky-600 via-sky-500 to-sky-400 rounded-2xl px-5 py-4 shadow-xl text-white border border-white/25 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.24em] text-sky-100/80">
                  Cobertura estratégica · Starlink
                </p>
                <p className="text-sm md:text-base text-sky-50 mt-1">
                  Estado actual de la infraestructura satelital operativa en Quintana Roo.
                </p>
              </div>

              <div className="flex flex-wrap gap-3 text-xs">
                <div className="bg-white/15 rounded-xl px-3 py-2 backdrop-blur-md border border-white/25 min-w-[110px]">
                  <p className="text-[10px] uppercase tracking-wide text-sky-100/90">
                    Municipios con registro
                  </p>
                  <p className="text-lg font-semibold leading-tight">
                    {Object.keys(modulosPorMunicipioCount).length}
                  </p>
                </div>

                <div className="bg-white/15 rounded-xl px-3 py-2 backdrop-blur-md border border-white/25 min-w-[110px]">
                  <p className="text-[10px] uppercase tracking-wide text-sky-100/90">
                    Módulos monitoreados
                  </p>
                  <p className="text-lg font-semibold leading-tight">
                    {totalModulosExistentes}
                  </p>
                </div>

                <div className="bg-white/15 rounded-xl px-3 py-2 backdrop-blur-md border border-white/25 min-w-[110px]">
                  <p className="text-[10px] uppercase tracking-wide text-sky-100/90">
                    Equipos asignados
                  </p>
                  <p className="text-lg font-semibold leading-tight">
                    {asignadosTotal}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-3 text-slate-900">
                Cobertura Starlink por Municipio
              </h3>
              <p className="text-xs text-slate-500 mb-4">
                Vista geográfica de Starlink en Quintana Roo, con antenas animadas y municipios resaltados.
              </p>

              <div className="w-full h-[500px] rounded-2xl overflow-hidden border border-slate-300/70 bg-slate-900/90 shadow-[0_20px_55px_rgba(15,23,42,0.6)]">
                <MapaQuintanaRoo />
              </div>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
}

function ExecutiveSummary({ total, stock, asignados, salud }) {
  return (
    <div className="w-[300px] rounded-2xl bg-[#0B1220] border border-[#1E293B] p-4 shadow-2xl">
      
      <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-3">
        Resumen Ejecutivo
      </p>

      <div className="space-y-1 text-sm">
        <div className="flex justify-between text-slate-300">
          <span>Equipos</span>
          <span className="font-semibold text-blue-400">{total}</span>
        </div>

        <div className="flex justify-between text-slate-300">
          <span>Stock</span>
          <span className="font-semibold text-emerald-400">{stock}</span>
        </div>

        <div className="flex justify-between text-slate-300">
          <span>Asignados</span>
          <span className="font-semibold text-indigo-400">{asignados}</span>
        </div>
      </div>

      <div className="mt-4">
        <div className="flex justify-between text-sm">
          <span className="text-slate-300">Salud</span>
          <span className="font-bold text-blue-400">{salud}%</span>
        </div>

        <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-sky-400"
            style={{ width: `${salud}%` }}
          />
        </div>
      </div>
    </div>
  );
}

